import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  ChevronRight,
  ShieldCheck,
  ShieldAlert,
  Shield,
  AlertCircle,
  Minus,
  ArrowLeft,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("academiaos_token");

// ── Status configs ─────────────────────────────────────────────────────────────

const EVAL_STATUS = {
  NOT_EVALUATED: { label: "Not Evaluated", color: "text-muted-foreground", bg: "bg-muted/50", border: "border-border", icon: Minus, dot: "bg-muted-foreground" },
  IN_PROGRESS:   { label: "In Progress",   color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-50 dark:bg-amber-950/30", border: "border-amber-200 dark:border-amber-800", icon: Clock, dot: "bg-amber-500" },
  BLOCKED:       { label: "Blocked",       color: "text-red-600 dark:text-red-400", bg: "bg-red-50 dark:bg-red-950/30", border: "border-red-200 dark:border-red-800", icon: XCircle, dot: "bg-red-500" },
  READY:         { label: "Ready",         color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-950/30", border: "border-emerald-200 dark:border-emerald-800", icon: CheckCircle2, dot: "bg-emerald-500" },
};

const LIFECYCLE_STATUS = {
  OPEN:       { label: "Open",      color: "text-sky-600 dark:text-sky-400", bg: "bg-sky-50 dark:bg-sky-950/30", border: "border-sky-200 dark:border-sky-800" },
  VALIDATED:  { label: "Validated", color: "text-violet-600 dark:text-violet-400", bg: "bg-violet-50 dark:bg-violet-950/30", border: "border-violet-200 dark:border-violet-800" },
  APPROVED:   { label: "Approved",  color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-950/30", border: "border-emerald-200 dark:border-emerald-800" },
  CLOSED:     { label: "Closed",    color: "text-muted-foreground", bg: "bg-muted/50", border: "border-border" },
};

const GATE_TYPE_BADGE = {
  GO_LIVE:  "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800",
  STANDARD: "bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800",
};

function EvalBadge({ status, isStale }) {
  const cfg = EVAL_STATUS[status] || EVAL_STATUS.NOT_EVALUATED;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color} ${cfg.border}`} data-testid={`eval-badge-${status}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${isStale ? "bg-amber-400 animate-pulse" : cfg.dot}`} />
      {cfg.label}{isStale ? " ·  Stale" : ""}
    </span>
  );
}

function LifecycleBadge({ status }) {
  const cfg = LIFECYCLE_STATUS[status] || LIFECYCLE_STATUS.OPEN;
  return (
    <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
}

// ── Institution Readiness Banner ──────────────────────────────────────────────

function ReadinessBanner({ summary }) {
  if (!summary) return null;
  const { institution_ready, go_live_gate_count, blocked_standard_gate_count, summary: msg } = summary;
  return (
    <div
      data-testid="institution-readiness-banner"
      className={`flex items-start gap-4 p-4 rounded-xl border mb-6 ${
        institution_ready
          ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800"
          : "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800"
      }`}
    >
      {institution_ready
        ? <ShieldCheck className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
        : <ShieldAlert className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${institution_ready ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"}`}>
          {institution_ready ? "Institution Ready for Go-Live" : "Go-Live Not Ready"}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">{msg}</p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-xs text-muted-foreground">{go_live_gate_count} GO_LIVE gate{go_live_gate_count !== 1 ? "s" : ""}</p>
        {blocked_standard_gate_count > 0 && (
          <p className="text-xs text-red-500 mt-0.5">{blocked_standard_gate_count} standard gate{blocked_standard_gate_count !== 1 ? "s" : ""} blocked</p>
        )}
      </div>
    </div>
  );
}

// ── Gate Card ─────────────────────────────────────────────────────────────────

function GateCard({ gate, onClick }) {
  const evalCfg = EVAL_STATUS[gate.latest_evaluation_status] || EVAL_STATUS.NOT_EVALUATED;
  const EvalIcon = evalCfg.icon;
  const isGoLive = gate.gate_type === "GO_LIVE";

  return (
    <div
      data-testid={`gate-card-${gate.gate_code}`}
      onClick={() => onClick(gate)}
      className={`group relative rounded-xl border bg-card p-4 cursor-pointer hover:shadow-md transition-all duration-150 hover:-translate-y-0.5 ${
        isGoLive ? "border-amber-200 dark:border-amber-800/60" : "border-border"
      }`}
    >
      {/* Gate type badge */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${GATE_TYPE_BADGE[gate.gate_type] || GATE_TYPE_BADGE.STANDARD}`}>
          {gate.gate_type.replace("_", " ")}
        </span>
        <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors mt-0.5" />
      </div>

      {/* Gate name */}
      <h3 className="text-sm font-semibold text-foreground leading-snug mb-1 line-clamp-2">
        {gate.gate_name}
      </h3>
      <p className="text-xs text-muted-foreground/70 font-mono mb-3">{gate.gate_code}</p>

      {/* Status row */}
      <div className="flex items-center gap-2 flex-wrap">
        <EvalBadge status={gate.latest_evaluation_status} isStale={gate.is_stale} />
        <LifecycleBadge status={gate.lifecycle_status} />
      </div>

      {/* Stale warning */}
      {gate.is_stale && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          <span>Re-evaluation required</span>
        </div>
      )}

      {/* Footer */}
      <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {gate.requirement_count} requirement{gate.requirement_count !== 1 ? "s" : ""}
        </span>
        {gate.last_evaluated_at && (
          <span className="text-xs text-muted-foreground">
            {new Date(gate.last_evaluated_at).toLocaleDateString()}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function SetupGates() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [gates, setGates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const [gatesRes, summaryRes] = await Promise.all([
        fetch(`${API}/api/setup/gates`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/setup/institution/readiness`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (!gatesRes.ok) throw new Error(`Gates: HTTP ${gatesRes.status}`);
      const gatesData = await gatesRes.json();
      setGates(gatesData);
      if (summaryRes.ok) setSummary(await summaryRes.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const goLiveGates = gates.filter((g) => g.gate_type === "GO_LIVE");
  const standardGates = gates.filter((g) => g.gate_type === "STANDARD");

  return (
    <div className="min-h-screen bg-background" data-testid="setup-gates-page">
      {/* Header */}
      <div className="border-b border-border bg-card px-6 py-5">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                data-testid="gates-back-btn"
                onClick={() => navigate("/setup")}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Configuration
              </button>
              <span className="text-muted-foreground/30">/</span>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-foreground/70" />
                <div>
                  <h1 className="text-xl font-bold text-foreground" data-testid="gates-page-title">
                    Readiness Gates
                  </h1>
                  <p className="text-xs text-muted-foreground">Configuration readiness & go-live checkpoints</p>
                </div>
              </div>
            </div>
            <button
              data-testid="gates-refresh-btn"
              onClick={fetchAll}
              disabled={loading}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2.5 py-1.5 rounded-md border border-border hover:border-foreground/30"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-5xl mx-auto px-6 py-6">

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 p-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 mb-6" data-testid="gates-error">
            <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            <button onClick={fetchAll} className="ml-auto text-xs text-red-600 underline">Retry</button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gates-loading">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-40 rounded-xl border border-border bg-muted/30 animate-pulse" />
            ))}
          </div>
        )}

        {/* Loaded */}
        {!loading && !error && (
          <>
            <ReadinessBanner summary={summary} />

            {/* GO_LIVE gates */}
            {goLiveGates.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Go-Live Gates</h2>
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">{goLiveGates.length}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="go-live-gates-grid">
                  {goLiveGates.map((g) => (
                    <GateCard key={g.id} gate={g} onClick={(gate) => navigate(`/setup/gates/${gate.id}`)} />
                  ))}
                </div>
              </div>
            )}

            {/* Standard gates */}
            {standardGates.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Standard Gates</h2>
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">{standardGates.length}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="standard-gates-grid">
                  {standardGates.map((g) => (
                    <GateCard key={g.id} gate={g} onClick={(gate) => navigate(`/setup/gates/${gate.id}`)} />
                  ))}
                </div>
              </div>
            )}

            {/* Empty */}
            {gates.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="gates-empty">
                <Shield className="h-12 w-12 text-muted-foreground/30 mb-4" />
                <p className="text-sm font-medium text-muted-foreground">No readiness gates configured</p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Configuration gates for this institution will appear here once created.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
